import type { IncidentReport } from '@lmc/ts-common'
import type {
  IncidentCaseV2Step,
  IncidentCaseV2StepSaveRequestRaw
} from '@lmc/validations'
import { incidentReportsConstants } from 'locale/incidentReports'
import type { ComponentType, RefObject } from 'react'
import DutyOfCandourStep from './steps/DutyOfCandourStep'
import FollowUpActionsStep from './steps/FollowUpActionsStep'
import ImmediateActionsStep from './steps/ImmediateActionsStep'
import IncidentInformationStep from './steps/IncidentInformationStep'
import InjuriesAndDamagesStep from './steps/InjuriesAndDamagesStep'
import InvestigationStep from './steps/InvestigationStep'
import NotificationsStep from './steps/NotificationsStep'
import PeopleInvolvedStep from './steps/PeopleInvolvedStep'
import RestrictivePracticesStep from './steps/RestrictivePracticesStep'
import DutyOfCandourReadPanel from './view/panels/DutyOfCandourReadPanel'
import FollowUpActionsReadPanel from './view/panels/FollowUpActionsReadPanel'
import ImmediateActionsReadPanel from './view/panels/ImmediateActionsReadPanel'
import IncidentInformationReadPanel from './view/panels/IncidentInformationReadPanel'
import InjuriesAndDamagesReadPanel from './view/panels/InjuriesAndDamagesReadPanel'
import InvestigationReadPanel from './view/panels/InvestigationReadPanel'
import NotificationsReadPanel from './view/panels/NotificationsReadPanel'
import PeopleInvolvedReadPanel from './view/panels/PeopleInvolvedReadPanel'
import RestrictivePracticesReadPanel from './view/panels/RestrictivePracticesReadPanel'

/** A step's values as they are sent: dates as ISO strings, all optional. */
export type IncidentCaseV2StepValues =
  IncidentCaseV2StepSaveRequestRaw['values']

/** What a step hands the shell, which owns Back, Next and saving. */
export type IncidentCaseV2StepHandle = {
  /** Shows this step's errors; resolves false to keep the user on the step. */
  validate: () => Promise<boolean>
  /** This step's fields only. The backend writes exactly the paths sent. */
  getValues: () => IncidentCaseV2StepValues
  /** Whether saving now would change the log's own fields, not just the case's. */
  changesLog: () => boolean
  /**
   * The values sent are now the saved ones. Returns whether the step still
   * differs from them, as it does when edited while the save was in flight.
   */
  markSaved: (sentValues: IncidentCaseV2StepValues) => boolean
  /**
   * Work that needs the saved case, such as uploading the files the step
   * queued. Runs after every save; resolves false to keep the user on the
   * step, having told them why.
   */
  afterSave?: () => Promise<boolean>
}

/** What a step's read-only panel on the case view receives. */
export type IncidentCaseV2ReadPanelProps = {
  incidentReport: IncidentReport
}

/**
 * The contract every step component implements: render its fields in
 * `IncidentCaseV2StepForm`, which registers `validate`, `getValues` and
 * `markSaved` on `handleRef`. `incidentReport` is the latest saved report, to
 * prefill from; a step never saves itself.
 */
export type IncidentCaseV2StepProps = {
  incidentReport: IncidentReport
  handleRef: RefObject<IncidentCaseV2StepHandle | null>
  /** Whether the step differs from what was last saved; drives the leave prompt. */
  onDirtyChange: (isDirty: boolean) => void
}

/** One step of the form; ids come from `IncidentCaseV2StepSchema`. */
type IncidentCaseV2StepDefinition = {
  id: IncidentCaseV2Step
  /** Locale key, resolved by the shell. Never a literal. */
  titleKey: string
  /** One line under the title saying when the step applies. */
  subtitleKey?: string
  /** Unset until the step is built; the shell then shows a placeholder. */
  Component?: ComponentType<IncidentCaseV2StepProps>
  /** The step's answers on the case view, read only. */
  ReadPanel?: ComponentType<IncidentCaseV2ReadPanelProps>
}

/** The nine steps of the v2 incident case form, in presentation order. */
export const INCIDENT_CASE_V2_STEPS: ReadonlyArray<IncidentCaseV2StepDefinition> =
  [
    {
      id: 'incidentInformation',
      titleKey: incidentReportsConstants.CASE_V2_STEP_INCIDENT_INFORMATION,
      subtitleKey:
        incidentReportsConstants.CASE_V2_STEP_INCIDENT_INFORMATION_SUBTITLE,
      Component: IncidentInformationStep,
      ReadPanel: IncidentInformationReadPanel
    },
    {
      id: 'immediateActions',
      titleKey: incidentReportsConstants.CASE_V2_STEP_IMMEDIATE_ACTIONS,
      subtitleKey:
        incidentReportsConstants.CASE_V2_STEP_IMMEDIATE_ACTIONS_SUBTITLE,
      Component: ImmediateActionsStep,
      ReadPanel: ImmediateActionsReadPanel
    },
    {
      id: 'peopleInvolved',
      titleKey: incidentReportsConstants.CASE_V2_STEP_PEOPLE_INVOLVED,
      subtitleKey:
        incidentReportsConstants.CASE_V2_STEP_PEOPLE_INVOLVED_SUBTITLE,
      Component: PeopleInvolvedStep,
      ReadPanel: PeopleInvolvedReadPanel
    },
    {
      id: 'restrictivePractices',
      titleKey: incidentReportsConstants.CASE_V2_STEP_RESTRICTIVE_PRACTICES,
      subtitleKey:
        incidentReportsConstants.CASE_V2_STEP_RESTRICTIVE_PRACTICES_SUBTITLE,
      Component: RestrictivePracticesStep,
      ReadPanel: RestrictivePracticesReadPanel
    },
    {
      id: 'injuriesAndDamages',
      titleKey: incidentReportsConstants.CASE_V2_STEP_INJURIES_AND_DAMAGES,
      subtitleKey:
        incidentReportsConstants.CASE_V2_STEP_INJURIES_AND_DAMAGES_SUBTITLE,
      Component: InjuriesAndDamagesStep,
      ReadPanel: InjuriesAndDamagesReadPanel
    },
    {
      id: 'followUpActions',
      titleKey: incidentReportsConstants.CASE_V2_STEP_FOLLOW_UP_ACTIONS,
      subtitleKey:
        incidentReportsConstants.CASE_V2_STEP_FOLLOW_UP_ACTIONS_SUBTITLE,
      Component: FollowUpActionsStep,
      ReadPanel: FollowUpActionsReadPanel
    },
    {
      id: 'investigation',
      titleKey: incidentReportsConstants.CASE_V2_STEP_INVESTIGATION,
      subtitleKey: incidentReportsConstants.CASE_V2_STEP_INVESTIGATION_SUBTITLE,
      Component: InvestigationStep,
      ReadPanel: InvestigationReadPanel
    },
    {
      id: 'dutyOfCandour',
      titleKey: incidentReportsConstants.CASE_V2_STEP_DUTY_OF_CANDOUR,
      subtitleKey:
        incidentReportsConstants.CASE_V2_STEP_DUTY_OF_CANDOUR_SUBTITLE,
      Component: DutyOfCandourStep,
      ReadPanel: DutyOfCandourReadPanel
    },
    {
      id: 'notifications',
      titleKey: incidentReportsConstants.CASE_V2_STEP_NOTIFICATIONS,
      subtitleKey: incidentReportsConstants.CASE_V2_STEP_NOTIFICATIONS_SUBTITLE,
      Component: NotificationsStep,
      ReadPanel: NotificationsReadPanel
    }
  ]

export const TOTAL_INCIDENT_CASE_V2_STEPS = INCIDENT_CASE_V2_STEPS.length
